/**
 * Service for fetching provider images from the API.
 */

import { getProviderImageUrl } from './configService';

interface ProviderImageResponse {
  provider_id: string;
  image_url: string;
}

/**
 * Fetches a provider image URL from the API.
 * 
 * @param providerCode - The provider code from availability data
 * @returns Promise<string> - The image URL or null if not found
 */
export async function fetchProviderImage(providerCode: string): Promise<string | null> {
  try {
    const baseUrl = getProviderImageUrl();
    const response = await fetch(`${baseUrl}${providerCode}`);
    
    if (!response.ok) {
      return null;
    }
    
    const data: ProviderImageResponse = await response.json();
    return data.image_url || null;
  } catch (error) {
    return null;
  }
}

/**
 * Fetches multiple provider images in parallel.
 * 
 * @param providerCodes - Array of provider codes
 * @returns Promise<Map<string, string>> - Map of provider code to image URL
 */
export async function fetchProviderImages(providerCodes: string[]): Promise<Map<string, string>> {
  const imageMap = new Map<string, string>();
  
  // Fetch all images in parallel
  const promises = providerCodes.map(async (code) => {
    const imageUrl = await fetchProviderImage(code);
    if (imageUrl) {
      imageMap.set(code, imageUrl);
    }
  });
  
  await Promise.all(promises);
  return imageMap;
}
